import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  Outlet,
  Link,
  createRootRouteWithContext,
  useLocation,
  useRouter,
  HeadContent,
  Scripts,
} from "@tanstack/react-router";
import type { ErrorComponentProps } from "@tanstack/react-router";
import { lazy, Suspense, useEffect, useState, type ReactNode } from "react";

import appCss from "../styles.css?url";
import { reportLovableError } from "../lib/lovable-error-reporting";
import { Toaster } from "@/components/ui/sonner";
import { LanguageBanner } from "@/components/LanguageBanner";
import { useSiteWakeLock } from "@/hooks/use-site-wake-lock";

import { useLocale } from "@/hooks/use-locale";
const BackgroundMusic = lazy(() =>
  import("@/components/BackgroundMusic").then((m) => ({ default: m.BackgroundMusic })),
);
const AutoTranslate = lazy(() =>
  import("@/components/AutoTranslate").then((m) => ({ default: m.AutoTranslate })),
);
const AiAssistant = lazy(() =>
  import("@/components/AiAssistant").then((m) => ({ default: m.AiAssistant })),
);
const OutboundTracker = lazy(() =>
  import("@/components/OutboundTracker").then((m) => ({ default: m.OutboundTracker })),
);

function NotFoundComponent() {
  const links: Array<{
    to: "/" | "/newsletter" | "/about" | "/contact";
    label: string;
    sub: string;
  }> = [
    { to: "/", label: "Today's discoveries", sub: "What's strange right now" },
    { to: "/newsletter", label: "Newsletter", sub: "Weekly weirdness, by email" },
    { to: "/about", label: "About Smartasaurus", sub: "Why we chase the strange" },
    { to: "/contact", label: "Contact", sub: "Tips, corrections, hellos" },
  ];
  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-background px-4 py-16">
      <div className="w-full max-w-2xl text-center">
        <h1 className="font-display text-7xl uppercase leading-none text-foreground md:text-9xl">
          404
        </h1>
        <h2 className="mt-4 font-display text-2xl uppercase tracking-tight text-foreground md:text-3xl">
          This rabbit hole has no bottom.
        </h2>
        <p className="mt-3 text-sm text-muted-foreground md:text-base">
          The page you were chasing doesn't exist - or it wandered off. Try one of these instead.
        </p>
        <div className="mt-10 grid grid-cols-1 gap-3 text-left sm:grid-cols-2">
          {links.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              className="group rounded-2xl border border-border bg-card p-4 transition hover:border-primary/50"
            >
              <div className="font-display text-base uppercase leading-tight text-foreground group-hover:text-primary">
                {l.label}
              </div>
              <div className="mt-1 text-xs text-muted-foreground">{l.sub}</div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}

function ErrorComponent({ error, reset }: ErrorComponentProps) {
  console.error(error);
  const router = useRouter();
  useEffect(() => {
    reportLovableError(error, { boundary: "tanstack_root_error_component" });
  }, [error]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-xl font-semibold tracking-tight text-foreground">
          Something glitched in the cosmos
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Try again or head back to the home page.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => {
              router.invalidate();
              reset();
            }}
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Try again
          </button>
          <a
            href="/"
            className="inline-flex items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
          >
            Go home
          </a>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { title: "Smartasaurus - Understand the World, One Discovery a Day" },
      {
        name: "description",
        content:
          "Weird animals, ocean mysteries, dangerous nature, space, the human body, and unexplained phenomena - daily, for curious minds.",
      },
      { name: "author", content: "Smartasaurus" },
      { name: "p:domain_verify", content: "e964d6f58b13c3f71e70189e49bf1e46" },
      { name: "theme-color", content: "#0a0a0a" },
      { property: "og:title", content: "Smartasaurus - Understand the World, One Discovery a Day" },
      {
        property: "og:description",
        content: "Short, fact-checked reads that make the world make sense - animals, oceans, space, your own body.",
      },
      { property: "og:type", content: "website" },
      { property: "og:site_name", content: "Smartasaurus" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:site", content: "@Smartasaurus" },
    ],
    links: [
      { rel: "stylesheet", href: appCss },
      { rel: "icon", type: "image/png", href: "/favicon.png" },
      { rel: "apple-touch-icon", sizes: "192x192", href: "/icon-192.png" },
      { rel: "manifest", href: "/site.webmanifest" },
      {
        rel: "alternate",
        type: "application/rss+xml",
        title: "Smartasaurus - Latest Discoveries",
        href: "https://smartasaurus.com/feed.xml",
      },
      { rel: "preconnect", href: "https://images.weserv.nl" },
      { rel: "dns-prefetch", href: "https://plausible.io" },
      { rel: "dns-prefetch", href: "https://www.googletagmanager.com" },
      {
        rel: "preload",
        as: "font",
        type: "font/woff2",
        href: "/__l5e/assets-v1/8c9d5d18-ade9-48a3-bd01-bf669a6e68c6/inter-latin.woff2",
        crossOrigin: "anonymous",
      },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebSite",
          name: "Smartasaurus",
          url: "https://smartasaurus.com",
          description:
            "Short, fact-checked discoveries about animals, oceans, space, the human body, and how the world really works.",
          publisher: {
            "@type": "Organization",
            name: "Smartasaurus",
            url: "https://smartasaurus.com",
          },
        }),
      },
      // Plausible - privacy-friendly, cookie-free analytics.
      // 'exclusions' variant filters admin/founder/health paths at Plausible so internal QA never inflates traffic.
      {
        defer: true,
        "data-domain": "smartasaurus.com",
        "data-exclude":
          "/_authenticated/**, /admin, /admin/**, /founder, /founder/**, /health, /health/**, /me, /me/**, /saved, /saved/**, /history, /history/**",
        src: "https://plausible.io/js/script.exclusions.outbound-links.tagged-events.js",
      },
      // Google Analytics 4 - auto page_view suppressed for internal browsers and admin paths.
      {
        async: true,
        src: "https://www.googletagmanager.com/gtag/js?id=G-T2SJLHZ8X1",
      },
      {
        children:
          "window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}window.gtag=gtag;gtag('js',new Date());try{if(localStorage.getItem('sa.internal')==='1'){localStorage.setItem('plausible_ignore','true');}}catch(e){}var __p=location.pathname;var __saI=/^\\/(admin|founder|health|me|saved|history|_authenticated)(\\/|$)/.test(__p)||(function(){try{return localStorage.getItem('sa.internal')==='1'}catch(e){return false}})();gtag('config','G-T2SJLHZ8X1',{send_page_view:!__saI});",
      },
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function RootShell({ children }: { children: ReactNode }) {
  return (
    <html lang="en" dir="ltr" className="dark" suppressHydrationWarning>
      <head>
        <HeadContent />
        <script
          dangerouslySetInnerHTML={{
            __html:
              "try{var q=new URLSearchParams(location.search).get('lang'),l=q||localStorage.getItem('sa.lang');if(l&&l!=='en'){document.documentElement.lang=l;document.documentElement.dir=l==='ar'?'rtl':'ltr';document.documentElement.setAttribute('data-sa-translation-pending','true')}}catch(e){}setInterval(function(){var d=document.documentElement;if(d.hasAttribute('data-sa-translation-pending')){var t=+d.getAttribute('data-sa-pt')||Date.now();d.setAttribute('data-sa-pt',t);if(Date.now()-t>1500){d.removeAttribute('data-sa-translation-pending');d.removeAttribute('data-sa-pt')}}else d.removeAttribute('data-sa-pt')},250);",
          }}
        />
      </head>
      <body>
        <a
          href="#main-content"
          className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-[100] focus:rounded-md focus:bg-primary focus:px-4 focus:py-2 focus:text-sm focus:font-semibold focus:text-primary-foreground"
        >
          Skip to content
        </a>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();
  const router = useRouter();
  // Keep the screen awake while people are reading/listening/watching.
  useSiteWakeLock();
  useEffect(() => {
    const INTERNAL_RX = /^\/(admin|founder|health|me|saved|history|_authenticated)(\/|$)/;
    const isInternal = () => {
      try {
        if (window.localStorage.getItem("sa.internal") === "1") return true;
      } catch {
        /* ignore */
      }
      return INTERNAL_RX.test(window.location.pathname);
    };
    const unsub = router.subscribe("onResolved", ({ toLocation }) => {
      if (INTERNAL_RX.test(toLocation.pathname) || isInternal()) return;
      const w = window as unknown as { gtag?: (...args: unknown[]) => void };
      if (typeof w.gtag === "function") {
        w.gtag("event", "page_view", {
          page_path: toLocation.pathname + toLocation.searchStr,
          page_location: window.location.href,
          page_title: document.title,
        });
      }
    });
    return () => unsub();
  }, [router]);

  return (
    <QueryClientProvider client={queryClient}>
      <Outlet />
      <Toaster />
      <LanguageBanner />
      <DeferredPublicTools />
      <LocaleTranslator />
    </QueryClientProvider>
  );
}

function PublicBackgroundMusic() {
  const pathname = useLocation({ select: (location) => location.pathname });
  const isInternal = /^\/(admin|founder|health|me|saved|history|_authenticated)(\/|$)/.test(
    pathname,
  );
  return isInternal ? null : <BackgroundMusic />;
}

// Page text (menus, quiz, cards) must translate as soon as a non-English
// reader lands - not only after their first tap. English visitors never load it;
// once loaded it stays mounted so switching back to English restores the text.
function LocaleTranslator() {
  const [lang] = useLocale();
  const [on, setOn] = useState(false);
  useEffect(() => {
    if (lang !== "en") setOn(true);
  }, [lang]);
  if (!on) return null;
  return (
    <Suspense fallback={null}>
      <AutoTranslate />
    </Suspense>
  );
}

function DeferredPublicTools() {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    const start = () => setReady(true);
    const events = ["pointerdown", "touchstart", "keydown"] as const;
    events.forEach((event) => window.addEventListener(event, start, { once: true, passive: true }));
    return () => {
      events.forEach((event) => window.removeEventListener(event, start));
    };
  }, []);
  if (!ready) return null;
  return (
    <Suspense fallback={null}>
      <PublicBackgroundMusic />
      <AiAssistant />
      <OutboundTracker />
    </Suspense>
  );
}
